import type {QueryClient} from '@tanstack/react-query';
import {parseCookies} from 'nookies';
import {useCallback, useState} from 'react';
import {z} from 'zod';
import type {ReportType} from '../../../core/entities/reportAnalytics/schema';
import {
  type GetEntryManagementReturnValueType,
  getEntryManagement,
  useCreateEntryManagement,
  useCreateStep,
} from '../../../core/repositories/mypageEntryManagementsRepository';
import {
  PROCESSING_STATE,
  type ProcessingState,
} from '../components/Container/Presenter/Progress/AiTodoProcessingContent';
import {ENTRY_SOURCE_TYPE_TODO_CREATION} from '../constants/entrySourceTypes';

/**
 * queryClientを使ってエントリー管理詳細を取得するヘルパー関数
 * useGetEntryManagementと同じクエリキーを使用してキャッシュを共有
 */
const fetchEntryManagementDetail = async (
  queryClient: QueryClient,
  id: number,
): Promise<GetEntryManagementReturnValueType | undefined> => {
  try {
    return await queryClient.fetchQuery({
      queryKey: ['entryManagement', id],
      queryFn: async () => {
        const cookies = parseCookies();
        return getEntryManagement({cookies, id});
      },
    });
  } catch (error) {
    console.error('[fetchEntryManagementDetail] Error:', error);
    return undefined;
  }
};

/**
 * refetchEntryManagementsの戻り値から配列を安全に取得するスキーマ
 */
const EntryManagementEntrySchema = z.object({
  id: z.number(),
  companyID: z.number(),
  companyName: z.string(),
});

const FetchResultSchema = z.object({
  data: z.object({
    ok: z.literal(true),
    value: z.object({
      entries: z.array(EntryManagementEntrySchema),
    }),
  }),
});

type EntryManagementEntry = z.infer<typeof EntryManagementEntrySchema>;

const extractEntriesFromFetchResult = (
  fetchResult: unknown,
): EntryManagementEntry[] => {
  const result = FetchResultSchema.safeParse(fetchResult);
  if (result.success) {
    return result.data.data.value.entries;
  }
  return [];
};

type EntryWithOptionalCompanyId = {
  id: number;
  companyID: number | null;
  companyName: string;
};

/**
 * 既存の選考管理を検索するヘルパー関数
 * companyIDとcompanyNameの両方でマッチするエントリーを優先し、
 * companyNameのみでマッチするエントリーをフォールバックとして返す
 */
export const findExistingEntry = <T extends EntryWithOptionalCompanyId>(
  entries: T[],
  companyId: number,
  companyName: string,
): T | undefined => {
  // companyIDとcompanyNameの両方でマッチするエントリーを優先
  const exactMatch = entries.find(
    (e) => e.companyID === companyId && e.companyName === companyName,
  );
  if (exactMatch) {
    return exactMatch;
  }

  // companyNameのみでマッチするエントリー（ユニーク制約: user_id + company_name）
  return entries.find((e) => e.companyName === companyName);
};

export type StepCandidate = {
  stepName: string;
  startDate?: string | null;
  endDate?: string | null;
  deadline?: string | null;
  place?: string;
  notes?: string;
};

type UseEntryStepCreationArgs = {
  queryClient: QueryClient;
  refetchEntryManagements: () => Promise<unknown>;
  existingEntryManagementId: number | null;
  entryDetailData:
    | {
        ok: true;
        value: {
          steps?: Array<{id: number; stepName: string}>;
        };
      }
    | {ok: false}
    | undefined;
};

/**
 * 既存の選考管理から最新のステップ情報を取得し、重複を除外するヘルパー関数
 */
const updateStepsWithLatestData = async (
  queryClient: QueryClient,
  entryManagementId: number,
  stepsToAdd: StepCandidate[],
  collectedSteps: {id: number; stepName: string}[],
): Promise<{
  remainingSteps: StepCandidate[];
  currentStepOrder: number;
  updatedCollectedSteps: {id: number; stepName: string}[];
}> => {
  const existingDetail = await fetchEntryManagementDetail(
    queryClient,
    entryManagementId,
  );

  if (existingDetail?.ok && existingDetail.value.steps) {
    const latestStepNames = new Set(
      existingDetail.value.steps.map((step) => step.stepName),
    );
    // 最新のステップ情報で重複フィルタ
    const remainingSteps = stepsToAdd.filter(
      (candidate) => !latestStepNames.has(candidate.stepName),
    );
    // 最新の既存ステップを新しい配列として返す
    const updatedCollectedSteps = existingDetail.value.steps.map((s) => ({
      id: s.id,
      stepName: s.stepName,
    }));
    return {
      remainingSteps,
      currentStepOrder: existingDetail.value.steps.length,
      updatedCollectedSteps,
    };
  }

  // 詳細取得に失敗した場合は元のフィルタ結果を使用
  return {
    remainingSteps: stepsToAdd,
    currentStepOrder: collectedSteps.length,
    updatedCollectedSteps: collectedSteps,
  };
};

export const useEntryStepCreation = ({
  queryClient,
  refetchEntryManagements,
  existingEntryManagementId,
  entryDetailData,
}: UseEntryStepCreationArgs) => {
  const [processingState, setProcessingState] = useState<ProcessingState>(
    PROCESSING_STATE.FETCHING_STEPS,
  );
  const [addedStepCount, setAddedStepCount] = useState(0);
  const [totalStepCount, setTotalStepCount] = useState(0);
  const [addedSteps, setAddedSteps] = useState<
    {id: number; stepName: string}[]
  >([]);
  const [userEntryManagementId, setUserEntryManagementId] = useState<
    number | null
  >(null);

  const createEntryManagement = useCreateEntryManagement();
  const createStep = useCreateStep();

  const addAllSteps = useCallback(
    async (
      stepCandidates: StepCandidate[],
      selectedCompanyId: number,
      selectedCompanyName: string,
      _departmentID: number,
      _reportType: ReportType,
    ) => {
      setProcessingState(PROCESSING_STATE.CHECKING_ENTRY);

      // 既存ステップ名を取得
      const existingStepNames = new Set(
        entryDetailData?.ok
          ? (entryDetailData.value.steps?.map((step) => step.stepName) ?? [])
          : [],
      );

      // 新しいステップのみフィルタ
      const stepsToAdd = stepCandidates.filter(
        (candidate) => !existingStepNames.has(candidate.stepName),
      );

      setTotalStepCount(stepsToAdd.length);

      // すべてスキップされた場合
      if (stepsToAdd.length === 0 && existingEntryManagementId) {
        const existingSteps = entryDetailData?.ok
          ? (entryDetailData.value.steps ?? [])
          : [];
        setUserEntryManagementId(existingEntryManagementId);
        setAddedSteps(
          existingSteps.map((s) => ({id: s.id, stepName: s.stepName})),
        );
        return {success: true, shouldProceedToStepSelect: true};
      }

      if (stepsToAdd.length === 0) {
        setProcessingState(PROCESSING_STATE.ERROR);
        return {success: false, shouldProceedToStepSelect: false};
      }

      setProcessingState(PROCESSING_STATE.ADDING_STEPS);
      let collectedSteps: {id: number; stepName: string}[] = [];

      // 既存ステップも含める
      if (existingEntryManagementId && entryDetailData?.ok) {
        const existingSteps = entryDetailData.value.steps ?? [];
        collectedSteps = existingSteps.map((s) => ({
          id: s.id,
          stepName: s.stepName,
        }));
      }

      let currentEntryManagementId = existingEntryManagementId;
      let currentStepOrder = entryDetailData?.ok
        ? (entryDetailData.value.steps?.length ?? 0)
        : 0;
      let remainingSteps: StepCandidate[] = [];
      // ステップ追加時に使用するcompanyID（既存エントリーが見つかった場合はそのcompanyIDを使用）
      let companyIdForSteps = selectedCompanyId;

      // 新規選考管理作成（既存がない場合）
      if (!currentEntryManagementId) {
        setProcessingState(PROCESSING_STATE.CREATING_ENTRY);

        // 最新データで既存エントリーを再チェック（500エラー回避のため）
        // ※Container側でもキャッシュを使ってチェック済みだが、
        //   別タブで作成済みなどキャッシュが古い場合に備えて最新データで確認
        const fetchResult = await refetchEntryManagements();
        const entries = extractEntriesFromFetchResult(fetchResult);
        const existingEntry = findExistingEntry(
          entries,
          selectedCompanyId,
          selectedCompanyName,
        );

        if (existingEntry) {
          // 既存のエントリー管理が見つかった場合はそれを使用
          currentEntryManagementId = existingEntry.id;
          setUserEntryManagementId(existingEntry.id);
          // 既存エントリーのcompanyIDを使用（selectedCompanyIdと異なる可能性がある）
          companyIdForSteps = existingEntry.companyID;

          // 既存の選考管理から最新のステップ情報を取得して重複を除外
          const updateResult = await updateStepsWithLatestData(
            queryClient,
            existingEntry.id,
            stepsToAdd,
            collectedSteps,
          );
          remainingSteps = updateResult.remainingSteps;
          currentStepOrder = updateResult.currentStepOrder;
          collectedSteps = updateResult.updatedCollectedSteps;
        } else {
          // 既存のエントリー管理がない場合は新規作成
          const firstStepName = stepsToAdd[0]?.stepName || '選考';

          try {
            const createResult = await createEntryManagement.mutateAsync({
              companyID: selectedCompanyId,
              companyName: selectedCompanyName,
              steps: [
                {
                  stepName: firstStepName,
                  stepOrder: 1,
                  entrySourceType: ENTRY_SOURCE_TYPE_TODO_CREATION,
                },
              ],
            });

            if (!createResult.ok) {
              // 作成失敗
              setProcessingState(PROCESSING_STATE.ERROR);
              return {success: false, shouldProceedToStepSelect: false};
            }

            // 作成成功
            currentEntryManagementId = createResult.value.id;
            setUserEntryManagementId(createResult.value.id);

            const createdFirstStep = createResult.value.steps[0];
            if (createdFirstStep) {
              collectedSteps.push({
                id: createdFirstStep.id,
                stepName: firstStepName,
              });
              currentStepOrder = 1;
              setAddedStepCount(1);
              // 最初のステップは作成済みなので残りのステップを設定
              remainingSteps = stepsToAdd.slice(1);
            } else {
              // ステップが返されなかった場合は全てのステップを追加対象とする
              remainingSteps = stepsToAdd;
            }
          } catch (error) {
            console.error('[Create Entry Management] Error:', error);
            setProcessingState(PROCESSING_STATE.ERROR);
            return {success: false, shouldProceedToStepSelect: false};
          }
        }

        // 選考管理一覧を再取得
        await queryClient.invalidateQueries(['paginatedEntryManagements']);
        setProcessingState(PROCESSING_STATE.ADDING_STEPS);
      } else {
        setUserEntryManagementId(currentEntryManagementId);

        // 既存の選考管理から最新のステップ情報を取得して重複を防ぐ
        const updateResult = await updateStepsWithLatestData(
          queryClient,
          currentEntryManagementId,
          stepsToAdd,
          collectedSteps,
        );
        remainingSteps = updateResult.remainingSteps;
        currentStepOrder = updateResult.currentStepOrder;
        collectedSteps = updateResult.updatedCollectedSteps;
      }

      // 残りのステップを順次追加
      for (const candidate of remainingSteps) {
        currentStepOrder += 1;
        try {
          const stepResult = await createStep.mutateAsync({
            companyID: companyIdForSteps,
            companyName: selectedCompanyName,
            stepName: candidate.stepName,
            stepOrder: currentStepOrder,
            entrySourceType: ENTRY_SOURCE_TYPE_TODO_CREATION,
          });

          if (stepResult.ok) {
            setAddedStepCount((prev) => prev + 1);
          }
        } catch (error) {
          console.error('[Add Step] Error:', error);
        }
      }

      // 選考管理データを再取得
      await refetchEntryManagements();
      await queryClient.invalidateQueries(['paginatedEntryManagements']);

      // サーバーから最新の選考管理詳細を取得し、全ステップを表示
      if (currentEntryManagementId) {
        const freshDetail = await fetchEntryManagementDetail(
          queryClient,
          currentEntryManagementId,
        );

        if (freshDetail?.ok && freshDetail.value.steps) {
          setAddedSteps(
            freshDetail.value.steps.map((s) => ({
              id: s.id,
              stepName: s.stepName,
            })),
          );
        } else {
          // サーバーからの取得に失敗した場合はローカルで収集したステップを使用
          setAddedSteps(collectedSteps);
        }
      } else {
        setAddedSteps(collectedSteps);
      }

      return {success: true, shouldProceedToStepSelect: true};
    },
    [
      entryDetailData,
      existingEntryManagementId,
      createEntryManagement,
      createStep,
      refetchEntryManagements,
      queryClient,
    ],
  );

  const resetStepCreation = useCallback(() => {
    setProcessingState(PROCESSING_STATE.FETCHING_STEPS);
    setAddedStepCount(0);
    setTotalStepCount(0);
    setAddedSteps([]);
    setUserEntryManagementId(null);
  }, []);

  return {
    processingState,
    setProcessingState,
    addedStepCount,
    totalStepCount,
    addedSteps,
    setAddedSteps,
    userEntryManagementId,
    setUserEntryManagementId,
    addAllSteps,
    resetStepCreation,
  };
};
